import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import { spliceLine, mskHhmmToMs, msToMskHhmm, type CategoryTreeLine } from '../constructor/lineModel';
import { serializeCategoryTree, type CategoryTreeFields } from '../constructor/treeBlock';
import { AutoSegSeeder, RackChart, useRackLayoutDetail, type AutoSeg } from './RackChart';
import type { ChartKeypoint } from '../chart/types';
import { Tooltip } from '@/components/ui/tooltip';

interface RackSeg extends AutoSeg {
  rackId: number;
}

type KpId = 'open' | 'vyshla' | 'popoln' | 'close';
const KP_COLOR: Record<KpId, string> = {
  open: '#f59e0b',
  vyshla: '#22c55e',
  popoln: '#ef4444',
  close: '#6366f1',
};

/**
 * Interactive editor for a "Выкладка" category tree block (F1+F2 merged):
 * per-rack shortage SEGMENTS drive "Просадки" (shelfCount/problemHours,
 * same aggregation FoodStatusChartBlock used for the old flat food_status
 * line), while fixed open/close KEYPOINTS (with a %-level slider, same
 * pattern as KeypointChartBlock) plus two conditionally-present draggable
 * ones ("Вышла в норму" / "Пополнялась в последний раз") edit the
 * opening/closing narrative. Both annotation kinds mount on the SAME
 * `RackChart` — `AnnotatableTimeChart` already supports segments and
 * keypoints simultaneously (segments/keypoints are independent props).
 */
export function CategoryTreeChartBlock({
  line,
  racks,
  zoneId,
  day,
  value,
  onChange,
  disabled,
}: {
  line: CategoryTreeLine;
  racks: { id: number; name: string }[];
  zoneId: string;
  day: string;
  value: string;
  onChange: (bodyHtml: string) => void;
  disabled?: boolean;
}) {
  const { t } = useT();
  const kb = line.fields;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [segs, setSegs] = useState<RackSeg[]>([]);
  // The intervals the model's verdict rests on («ниже нормы N ч» is the
  // union of exactly these per-shelf shortage windows, `compute_rack_auto
  // _segments`) are seeded onto every rack's chart once, so the operator
  // sees WHAT the tree line is about instead of a blank plot (Alexey
  // 2026-09-08). Seeded for ALL racks of the category, not just the visible
  // pill, so the aggregate a later edit recomputes counts every rack.
  const [seededRacks, setSeededRacks] = useState<Set<number>>(new Set());
  const seedRack = (rackId: number, auto: AutoSeg[]) => {
    setSeededRacks((prev) => new Set(prev).add(rackId));
    if (auto.length) setSegs((prev) => [...prev, ...auto.map((seg) => ({ ...seg, rackId }))]);
  };
  // #19: a category with several racks stacked every rack's chart at full
  // height and the block ran off the viewport. Default to ONE rack at a
  // time behind a pill picker (same convention as the report's rack
  // dropdown), with «Все» to stack them on purpose.
  const [activeRack, setActiveRack] = useState<number | 'all'>(racks.length > 1 ? racks[0].id : 'all');
  const [pctOverride, setPctOverride] = useState<{ id: string; value: number } | null>(null);
  useEffect(() => setPctOverride(null), [selectedId]);

  const { data: layout } = useRackLayoutDetail(zoneId, day, racks[0]?.id);
  const openHhmm = useMemo(() => {
    const start = layout?.working_hours?.start;
    return start ? msToMskHhmm(new Date(start).getTime()) : null;
  }, [layout?.working_hours?.start]);
  const closeHhmm = useMemo(() => {
    const end = layout?.working_hours?.end;
    // item 10c convention (matches KeypointChartBlock): closing anchors 20
    // min before the literal close instant everywhere in this system.
    return end ? msToMskHhmm(new Date(end).getTime() - 20 * 60_000) : null;
  }, [layout?.working_hours?.end]);
  const openMs = openHhmm ? mskHhmmToMs(day, openHhmm) : null;
  const closeMs = closeHhmm ? mskHhmmToMs(day, closeHhmm) : null;
  const vyshlaMs = kb.filledAt ? mskHhmmToMs(day, kb.filledAt) : null;
  const popolnMs = kb.replenishedAt ? mskHhmmToMs(day, kb.replenishedAt) : null;

  const push = (patch: Partial<CategoryTreeFields>) =>
    onChange(spliceLine(value, line, serializeCategoryTree({ ...kb, ...patch })));

  const pushAggregate = (next: RackSeg[]) => {
    const rackIds = new Set(next.map((s) => s.rackId));
    const problemHours = Math.round((next.reduce((a, s) => a + (s.t_end - s.t_start), 0) / 3_600_000) * 10) / 10;
    push({ shelfCount: rackIds.size, problemHours });
  };

  const updateSegs = (fn: (arr: RackSeg[]) => RackSeg[]) => {
    setSegs((prev) => {
      const next = fn(prev);
      pushAggregate(next);
      return next;
    });
  };

  const showVyshla = !kb.neverFilled && kb.openingProblem;
  const showPopoln = !kb.neverFilled && kb.closingProblem && Boolean(kb.replenishedAt);

  const keypoints: ChartKeypoint[] = useMemo(() => {
    const out: ChartKeypoint[] = [];
    if (openMs != null)
      out.push({
        id: 'open',
        t: openMs,
        color: KP_COLOR.open,
        fixed: true,
        fixedTooltip: 'Открытие — время фиксировано, двигать нельзя',
        level: kb.openingPct,
      });
    if (showVyshla && vyshlaMs != null) out.push({ id: 'vyshla', t: vyshlaMs, color: KP_COLOR.vyshla });
    if (showPopoln && popolnMs != null) out.push({ id: 'popoln', t: popolnMs, color: KP_COLOR.popoln });
    if (closeMs != null)
      out.push({
        id: 'close',
        t: closeMs,
        color: KP_COLOR.close,
        fixed: true,
        fixedTooltip: 'Закрытие (за 20 мин. до факт. закрытия) — время фиксировано, двигать нельзя',
        level: kb.closingPct,
      });
    return out;
  }, [openMs, closeMs, vyshlaMs, popolnMs, showVyshla, showPopoln, kb.openingPct, kb.closingPct]);

  const handleKeypointChange = (kp: ChartKeypoint) => {
    if (kp.id === 'vyshla') push({ filledAt: msToMskHhmm(kp.t) });
    else if (kp.id === 'popoln') push({ replenishedAt: msToMskHhmm(kp.t) });
  };

  const sel = keypoints.find((k) => k.id === selectedId) ?? null;
  const missing: KpId[] = [
    ...(!kb.neverFilled && !kb.openingProblem ? (['vyshla'] as KpId[]) : []),
    ...(!kb.neverFilled && !(kb.closingProblem && Boolean(kb.replenishedAt)) ? (['popoln'] as KpId[]) : []),
  ];

  const readout = (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
      {sel ? (
        <>
          <span className="font-medium">
            {sel.id === 'open' && t('reportValidation.tf.kp_open')}
            {sel.id === 'close' && t('reportValidation.tf.kp_close')}
            {sel.id === 'vyshla' && t('reportValidation.tf.normTime')}
            {sel.id === 'popoln' && t('reportValidation.tree.replenishedAt')}
          </span>
          <span className="text-muted-foreground">{msToMskHhmm(sel.t)} МСК</span>
          {(sel.id === 'open' || sel.id === 'close') &&
            (() => {
              const kbPct = sel.id === 'open' ? kb.openingPct : kb.closingPct;
              const shownPct = pctOverride && pctOverride.id === sel.id ? pctOverride.value : kbPct;
              return (
                <div className="flex w-full items-center gap-2 pt-1">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    disabled={disabled}
                    value={Math.round(shownPct / 5) * 5}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setPctOverride({ id: sel.id, value: v });
                      if (sel.id === 'open') push({ openingPct: v });
                      else push({ closingPct: v });
                    }}
                    className="flex-1 accent-primary"
                  />
                  <span className="w-10 shrink-0 text-right font-medium text-foreground">{Math.round(shownPct)}%</span>
                </div>
              );
            })()}
          {sel.id === 'vyshla' && (
            <Tooltip content={t('reportValidation.interactive.removeKeypoint')}><button
              type="button"
              disabled={disabled}
              onClick={() => {
                push({ openingProblem: false, filledAt: '' });
                setSelectedId(null);
              }}
              className="ml-auto rounded-md border border-border p-1 text-destructive hover:bg-destructive/10"
              aria-label={t('reportValidation.interactive.removeKeypoint')}
            >
              <Trash2 className="size-3.5" />
            </button></Tooltip>
          )}
          {sel.id === 'popoln' && (
            <Tooltip content={t('reportValidation.interactive.removeKeypoint')}><button
              type="button"
              disabled={disabled}
              onClick={() => {
                push({ replenishedAt: '' });
                setSelectedId(null);
              }}
              className="ml-auto rounded-md border border-border p-1 text-destructive hover:bg-destructive/10"
              aria-label={t('reportValidation.interactive.removeKeypoint')}
            >
              <Trash2 className="size-3.5" />
            </button></Tooltip>
          )}
        </>
      ) : (
        <>
          {missing.map((id) => (
            <button
              key={id}
              type="button"
              disabled={disabled}
              onClick={() => {
                const time = openHhmm ?? '12:00';
                if (id === 'vyshla') push({ openingProblem: true, filledAt: kb.filledAt || time });
                else push({ closingProblem: true, replenishedAt: kb.replenishedAt || time });
              }}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-0.5 text-muted-foreground hover:border-primary hover:text-primary"
            >
              <Plus className="size-3" />
              {id === 'vyshla' ? t('reportValidation.tf.normTime') : t('reportValidation.tree.replenishedAt')}
            </button>
          ))}
        </>
      )}
    </div>
  );

  if (kb.variant !== 'issues') return null;

  return (
    <div className="flex flex-col gap-3">
      {racks.map((rack) =>
        seededRacks.has(rack.id) ? null : (
          <AutoSegSeeder
            key={`seed-${rack.id}`}
            zoneId={zoneId}
            day={day}
            rackId={rack.id}
            alreadySeeded={seededRacks.has(rack.id)}
            onSeed={seedRack}
          />
        ),
      )}
      {/* No colour picker: the circle is derived from the parameters by
          serializeCategoryTree (Alexey 2026-09-08: «кружки всегда автоматом,
          разметчики делают только параметры»). */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <label className="inline-flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={kb.neverFilled}
            disabled={disabled}
            onChange={(e) => {
              const neverFilled = e.target.checked;
              // Same stale-state guard as CategoryTreeForm.tsx: a block
              // parsed out of the never-filled fallback carries a forced
              // openingProblem=true with no real filledAt behind it (see
              // parseCategoryTreeFields). Left alone, turning neverFilled
              // off here would leave the "Вышла в норму" keypoint in a
              // dead-end state — showVyshla true but vyshlaMs null (no
              // filledAt to place it), and the "+" button to add one
              // hidden too (missing[] only offers it when openingProblem
              // is false) — no way to fix it from this UI at all.
              if (!neverFilled && kb.openingProblem && !kb.filledAt) {
                push({ neverFilled, openingProblem: false });
              } else {
                push({ neverFilled });
              }
            }}
            className="size-3.5"
          />
          {t('reportValidation.kp.neverFilled')}
        </label>
      </div>

      {racks.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {racks.map((rack) => {
            const n = segs.filter((s) => s.rackId === rack.id).length;
            const active = activeRack === rack.id;
            return (
              <button
                key={rack.id}
                type="button"
                onClick={() => setActiveRack(rack.id)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 transition',
                  active ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {rack.name}
                {n > 0 && <span className="rounded-full bg-primary/15 px-1 text-[10px] tabular-nums text-primary">{n}</span>}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setActiveRack('all')}
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-0.5 transition',
              activeRack === 'all' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {t('report.day.allRacks')}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {racks.filter((rack) => activeRack === 'all' || rack.id === activeRack).map((rack) => {
          const rackSegs = segs.filter((s) => s.rackId === rack.id);
          return (
            <RackChart
              key={rack.id}
              zoneId={zoneId}
              day={day}
              rackId={rack.id}
              rackName={rack.name}
              annotateCreates="segment"
              segments={rackSegs}
              keypoints={keypoints}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onSegmentCreate={(seg, missingPct) => {
                updateSegs((arr) => [...arr, { ...seg, missingPct, rackId: rack.id }]);
                setSelectedId(seg.id);
              }}
              onSegmentChange={(seg, missingPct) =>
                updateSegs((arr) => arr.map((s) => (s.id === seg.id ? { ...s, ...seg, missingPct } : s)))
              }
              onKeypointChange={handleKeypointChange}
              addSegmentLabel={t('reportValidation.interactive.addProblem')}
              readout={readout}
            />
          );
        })}
      </div>

      {segs.length > 0 && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => updateSegs(() => [])}
          className="inline-flex w-fit items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition hover:bg-muted hover:text-foreground"
        >
          <Trash2 className="size-3.5" /> {t('reportValidation.interactive.removeSegment')}
        </button>
      )}
    </div>
  );
}
